import React, { useMemo } from "react";
import ManagementModal from "../../../components/ui/modals/ManagementModal";
import { useAppSelector } from "../../../app/store";
import { selectAssetEntities } from "../Logic/assetsSlice";
import { ChargeWithLinkages} from "../../../../src/api/charges";
import { getAccountProductInfoByAccountNumberMap } from "../../creditAccounts/logic/slice";

export interface ReviewSection {
  title: string;
  values: string[];
  action: "ADDED" | "REMOVED" | "UPDATED";
}

interface ReviewChargeChangesModalProps {
  existingCharge?: ChargeWithLinkages;
  updatedCharge?: ChargeWithLinkages;
  onBack: () => void;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

const formatEnumLabel = (value?: string) =>
  value ? value.split("_").map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(" ") : "";

const BadgeConfig = {
  ADDED: { text: "Added", classes: "bg-[#DCFCE7] text-[#16A34A]" },
  REMOVED: { text: "Removed", classes: "bg-[#FEE2E2] text-[#DC2626]" },
  UPDATED: { text: "Updated", classes: "bg-[#DBEAFE] text-[#2563EB]" },
};

const ActionBadge: React.FC<{ action: ReviewSection["action"] }> = ({ action }) => {
  const badge = BadgeConfig[action];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-medium leading-4 ${badge.classes}`}>
      {badge.text}
    </span>
  );
};

const ReviewChargeChangesModal: React.FC<ReviewChargeChangesModalProps> = ({
  existingCharge,
  updatedCharge,
  onBack,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const assetEntities = useAppSelector(selectAssetEntities);
  const accountByNumber = useAppSelector(getAccountProductInfoByAccountNumberMap);

  const reviewSections = useMemo<ReviewSection[]>(() => {
    if (!updatedCharge) return [];

    const toDisplayLoanAccount = (loanReferenceId: string) =>
      accountByNumber[loanReferenceId]?.ext_account_number || "--";

    const sections: ReviewSection[] = [];

    if (!existingCharge) {
      sections.push({
        title: "Assets Added",
        values: updatedCharge.asset_linkages.map(l => assetEntities[l.asset_id]?.ext_asset_id ?? l.asset_id),
        action: "ADDED",
      });

      sections.push({
        title: "Charge Information",
        values: [
          `Security Role: ${formatEnumLabel(updatedCharge.charges.security_role)}`,
          `Charge Mode: ${formatEnumLabel(updatedCharge.charges.charge_mode)}`,
          `Distribution Rights: ${formatEnumLabel(updatedCharge.charges.distribution_rights)}`,
          `Public Registry: ${updatedCharge.charges.public_registry}`,
          ...Object.entries(updatedCharge.charges.registry_identifiers ?? {}).map(
            ([key, value]) => `${formatEnumLabel(key)}: ${value}`
          ),
        ],
        action: "ADDED",
      });

      sections.push({
        title: "Loans Added",
        values: updatedCharge.loan_linkages.map((l) =>
          toDisplayLoanAccount(l.loan_reference_id),
        ),
        action: "ADDED",
      });

      return sections;
    }

    const existingAssetIds = new Set(existingCharge.asset_linkages.map(l => l.asset_id));
    const updatedAssetIds = new Set(updatedCharge.asset_linkages.map(l => l.asset_id));
    const addedAssets = Array.from(updatedAssetIds).filter(id => !existingAssetIds.has(id));

    if (addedAssets.length > 0) {
      sections.push({ 
        title: "Assets Added", 
        values: addedAssets.map(id => assetEntities[id]?.ext_asset_id ?? id),
        action: "ADDED" 
      });
    }

    const existingLoanIds = new Set(existingCharge.loan_linkages.map(l => l.loan_reference_id));
    const updatedLoanIds = new Set(updatedCharge.loan_linkages.map(l => l.loan_reference_id));
    const addedLoans = Array.from(updatedLoanIds).filter(id => !existingLoanIds.has(id));

    if (addedLoans.length > 0) {
      sections.push({
        title: "Loans Added",
        values: addedLoans.map(toDisplayLoanAccount),
        action: "ADDED",
      });
    }

    return sections;
  }, [existingCharge, updatedCharge, assetEntities, accountByNumber]);

  return (
    <ManagementModal
      title="Save Changes"
      subtitle="Are you sure you want to make changes. Review the changes before saving"
      onClose={onClose}
      onBack={onBack}
      onSubmit={onConfirm}
      submitLabel="Confirm & Save"
      cancelLabel="Discard"
      onCancel={onClose}
      isLoading={isLoading}
    >
      <div className="flex flex-col gap-3">
        {reviewSections.map((section) => (
          <div
            key={`${section.title}-${section.action}`}
            className="flex items-center gap-4 rounded-[8px] bg-[#F5F5F5] p-3"
          >
            <div className="flex flex-1 flex-col gap-1">
              <span className="text-xs font-normal leading-4 text-[#737373]">{section.title}</span>
              <div className="flex flex-col gap-1">
                {section.values.map((value, index) => (
                  <span
                    key={index}
                    className="text-sm font-normal leading-5 text-[#0A0A0A] truncate max-w-[450px]"
                  >
                    {value}
                  </span>
                ))}
              </div>
            </div>

            <div className="shrink-0">
              <ActionBadge action={section.action} />
            </div>
          </div>
        ))}

        {reviewSections.length === 0 && (
          <div className="rounded-[8px] bg-[#F5F5F5] p-6 text-center">
            <div className="text-sm text-[#737373]">No changes detected.</div>
          </div>
        )}
      </div>
    </ManagementModal>
  );
};

export default ReviewChargeChangesModal;